import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/store';
import { fetchResourceLevels } from '@/store/slices/resourceLevelsSlice';
import { Button, Input } from '@/components/ui';
import type { Resource, ResourceType } from '@/types';

interface ResourceFormProps {
  initialData?: Resource | null;
  onSubmit: (data: Partial<Resource>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const ResourceForm: React.FC<ResourceFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { entities: resourceLevels } = useSelector((state: RootState) => state.resourceLevels);
  const isEditMode = !!initialData;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    resourceType: 'EMPLOYEE' as ResourceType,
    resourceLevelId: '',
    isActive: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    dispatch(fetchResourceLevels());
  }, [dispatch]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        firstName: initialData.firstName,
        lastName: initialData.lastName,
        resourceType: initialData.resourceType,
        resourceLevelId: initialData.resourceLevelId,
        isActive: initialData.isActive,
      });
    }
  }, [initialData]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.resourceType) {
      newErrors.resourceType = 'Resource type is required';
    }

    if (!formData.resourceLevelId) {
      newErrors.resourceLevelId = 'Resource level is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Resource ID - Auto-generated for new resources, read-only for existing */}
      {isEditMode && (
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-foreground">
            Resource ID
          </label>
          <div className="h-9 px-3 flex items-center bg-muted border border-border rounded-md">
            <span className="font-mono text-sm text-foreground">{initialData?.resourceId}</span>
            <span className="ml-2 text-xs text-muted-foreground">(cannot be changed)</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="First Name"
          value={formData.firstName}
          onChange={(e) => handleInputChange('firstName', e.target.value)}
          placeholder="John"
          error={errors.firstName}
          required
        />

        <Input
          label="Last Name"
          value={formData.lastName}
          onChange={(e) => handleInputChange('lastName', e.target.value)}
          placeholder="Doe"
          error={errors.lastName}
          required
        />
      </div>

      <div className="space-y-1.5">
        <label htmlFor="resource-type" className="block text-sm font-medium text-foreground">
          Resource Type <span className="text-status-error">*</span>
        </label>
        <select
          id="resource-type"
          className={`w-full h-9 px-3 text-sm border rounded-md bg-[var(--surface)] text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] ${
            errors.resourceType ? 'border-status-error' : 'border-[var(--border)]'
          }`}
          value={formData.resourceType}
          onChange={(e) => handleInputChange('resourceType', e.target.value)}
        >
          <option value="EMPLOYEE">Employee</option>
          <option value="CONTRACTOR">Contractor</option>
          <option value="GDP_ONSHORE">GDP Onshore</option>
          <option value="GDP_OFFSHORE">GDP Offshore</option>
          <option value="INTERN">Intern</option>
        </select>
        {errors.resourceType && (
          <p className="text-xs text-status-error font-medium">{errors.resourceType}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <label htmlFor="resource-level" className="block text-sm font-medium text-foreground">
          Resource Level <span className="text-status-error">*</span>
        </label>
        <select
          id="resource-level"
          className={`w-full h-9 px-3 text-sm border rounded-md bg-[var(--surface)] text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] ${
            errors.resourceLevelId ? 'border-status-error' : 'border-[var(--border)]'
          }`}
          value={formData.resourceLevelId}
          onChange={(e) => handleInputChange('resourceLevelId', e.target.value)}
        >
          <option value="">Select Resource Level...</option>
          {resourceLevels.filter(rl => rl.isActive && rl.category === 'TECHNICAL').length > 0 && (
            <optgroup label="Technical Levels">
              {resourceLevels
                .filter(rl => rl.isActive && rl.category === 'TECHNICAL')
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(level => (
                  <option key={level.id} value={level.id}>
                    {level.code} - {level.displayName}
                  </option>
                ))}
            </optgroup>
          )}
          {resourceLevels.filter(rl => rl.isActive && rl.category === 'MANAGEMENT').length > 0 && (
            <optgroup label="Management Levels">
              {resourceLevels
                .filter(rl => rl.isActive && rl.category === 'MANAGEMENT')
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(level => (
                  <option key={level.id} value={level.id}>
                    {level.code} - {level.displayName}
                  </option>
                ))}
            </optgroup>
          )}
          {resourceLevels.filter(rl => rl.isActive && rl.category === 'SPECIALIZED').length > 0 && (
            <optgroup label="Specialized">
              {resourceLevels
                .filter(rl => rl.isActive && rl.category === 'SPECIALIZED')
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(level => (
                  <option key={level.id} value={level.id}>
                    {level.code} - {level.displayName}
                  </option>
                ))}
            </optgroup>
          )}
        </select>
        {errors.resourceLevelId && (
          <p className="text-xs text-status-error font-medium">{errors.resourceLevelId}</p>
        )}
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="isActive"
          checked={formData.isActive}
          onChange={(e) => handleInputChange('isActive', e.target.checked)}
          className="h-4 w-4 text-[var(--accent)] focus:ring-[var(--accent)] border-[var(--border)] rounded bg-[var(--surface)]"
        />
        <label htmlFor="isActive" className="ml-2 block text-xs text-foreground">
          Active (resource is currently available)
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isLoading}>
          {isLoading ? 'Saving...' : isEditMode ? 'Update Resource' : 'Create Resource'}
        </Button>
      </div>
    </form>
  );
};

export default ResourceForm;
